/* @flow */

import * as React from 'react'
import styled from 'styled-components'

import {
  ControlLabel,
  EditorInput,
  FilesInput,
  FilesExistingValue,
  FormInput,
  FormGroup,
  RadioInput,
  SelectInput,
  unwrapTemporaryFile,
} from '../../../../../infrastructure/components/Formik'
import { useWebshopNavigationLinks } from '../../../../settings/hooks'
import { useWebshopDesignPagesList } from '../../../hooks'
import {
  NAVIGATION_LINK_TYPE_SEARCH,
  NAVIGATION_LINK_TYPE_PAGE,
  NAVIGATION_LINK_TYPE_PAGE_V2,
  NAVIGATION_LINK_TYPE_BESTSELLERS,
} from '../../../shared'
import { SettingsSeparator, SpacingSettings } from '../shared'

const WebshopBannerSettings = ({ brand, values }) => {
  const [webshopNavigationLinks] = useWebshopNavigationLinks()
  const [webshopDesignPages] = useWebshopDesignPagesList()

  const webshopDesignPagesOptions = React.useMemo(() => {
    return webshopDesignPages
      .filter(p => p.module === 'page')
      .map(page => ({
        label: page.content_page_title,
        value: page.id,
      }))
  }, [webshopDesignPages])

  const availableNavigationLinks = React.useMemo(() => {
    // Other types than search, page and bestsellers can be navigated to via URL,
    // so we offer only the few basic types
    return webshopNavigationLinks.filter(link =>
      [
        NAVIGATION_LINK_TYPE_SEARCH,
        NAVIGATION_LINK_TYPE_PAGE,
        NAVIGATION_LINK_TYPE_PAGE_V2,
        NAVIGATION_LINK_TYPE_BESTSELLERS,
      ].includes(link.type)
    )
  }, [webshopNavigationLinks])

  return (
    <div>
      <FormGroup>
        <ControlLabel required>Click on banner leads to</ControlLabel>
        <RadioInput
          id="webshop_banner_settings_link_type_disabled"
          name="data.link_type"
          label="Does nothing"
          value="disabled"
        />
        <RadioInput
          id="webshop_banner_settings_link_type_navigation_link"
          name="data.link_type"
          label="Navigation link"
          value="navigation_link"
        />
        <RadioInput
          id="webshop_banner_settings_link_type_webshop_page"
          name="data.link_type"
          label="Webshop page"
          value="webshop_page"
        />
        <RadioInput
          id="webshop_banner_settings_link_type_manual_url"
          name="data.link_type"
          label="Manual URL"
          value="manual_url"
        />
      </FormGroup>
      {values.data.link_type === 'navigation_link' && (
        <FormGroup>
          <ControlLabel required>Navigation link</ControlLabel>
          <SelectInput
            name="data.navigation_link_id"
            options={availableNavigationLinks}
            simpleValue
            labelKey="text"
            valueKey="id"
          />
        </FormGroup>
      )}
      {values.data.link_type === 'webshop_page' && (
        <FormGroup>
          <ControlLabel required>Webshop page</ControlLabel>
          <SelectInput
            name="data.webshop_page_id"
            options={webshopDesignPagesOptions}
            simpleValue
          />
        </FormGroup>
      )}
      {values.data.link_type === 'manual_url' && (
        <FormGroup>
          <ControlLabel required>URL</ControlLabel>
          <FormInput name="data.manual_url" />
        </FormGroup>
      )}

      <SettingsSeparator />

      <WebshopBannerBaseSettingsForm values={values} />
    </div>
  )
}

export default WebshopBannerSettings

// also used by DropLinks
export const WebshopBannerBaseSettingsForm = ({ values }) => {
  const [replaceBanner, setReplaceBanner] = React.useState(false)

  const { unwrappedBannerValue } = React.useMemo(() => {
    let unwrappedBannerValue

    if (values.data.banner) {
      unwrappedBannerValue = unwrapTemporaryFile(values.data.banner)
    }

    return {
      unwrappedBannerValue,
    }
  }, [values.data.banner])

  return (
    <>
      <FormGroup>
        <ControlLabel required>Banner image or video</ControlLabel>
        {(replaceBanner || !unwrappedBannerValue) && (
          <FilesInput allowMultiple={false} name="data.banner" />
        )}
        <FilesExistingValue
          onChange={() => setReplaceBanner(true)}
          shouldChange={replaceBanner}
          value={unwrappedBannerValue}
        />
      </FormGroup>
      <FormGroup>
        <ControlLabel required>Height</ControlLabel>
        <HeightInputsContainer>
          <HeightModeSelectContainer>
            <SelectInput
              name="data.height_mode"
              options={heightModeOptions}
              simpleValue
              clearable={false}
            />
          </HeightModeSelectContainer>
          {values.data.height_mode === 'viewport_percentage' && (
            <>
              <HeightValueInputContainer>
                <FormInput
                  name="data.viewport_percentage_height"
                  type="number"
                  max={100}
                  min={1}
                />
              </HeightValueInputContainer>
              <div>%</div>
            </>
          )}
          {values.data.height_mode === 'fixed' && (
            <>
              <HeightValueInputContainer>
                <FormInput name="data.fixed_height" type="number" />
              </HeightValueInputContainer>
              <div>px</div>
            </>
          )}
        </HeightInputsContainer>
      </FormGroup>

      <SettingsSeparator />

      <FormGroup>
        <ControlLabel required>Buttons/links position</ControlLabel>
        <RadioInput
          id="inner_elements_position_center"
          name="data.inner_elements_position"
          label="Center"
          value="center"
        />
        <RadioInput
          id="inner_elements_position_top_left"
          name="data.inner_elements_position"
          label="Top left corner"
          value="top_left"
        />
        <RadioInput
          id="inner_elements_position_top_right"
          name="data.inner_elements_position"
          label="Top right corner"
          value="top_right"
        />
        <RadioInput
          id="inner_elements_position_bottom_left"
          name="data.inner_elements_position"
          label="Bottom left corner"
          value="bottom_left"
        />
        <RadioInput
          id="inner_elements_position_bottom_right"
          name="data.inner_elements_position"
          label="Bottom right corner"
          value="bottom_right"
        />
      </FormGroup>
      <FormGroup>
        <ControlLabel required>Button/links stack-mode</ControlLabel>
        <RadioInput
          id="inner_elements_stack_mode_side_by_side"
          name="data.inner_elements_stack_mode"
          label="List buttons/links side-by-side"
          value="side_by_side"
        />
        <RadioInput
          id="inner_elements_stack_mode_stacked"
          name="data.inner_elements_stack_mode"
          label="List buttons/links on top of each other"
          value="stacked"
        />
      </FormGroup>
      <FormGroup>
        <ControlLabel required>
          Button/links space between each (px)
        </ControlLabel>
        <FormInput name="data.inner_elements_space_between_each" />
      </FormGroup>

      <SettingsSeparator />

      <SpacingSettings />
    </>
  )
}

const heightModeOptions = [
  { label: 'Screen percentage', value: 'viewport_percentage' },

  // TODO: option not implemented yet
  // { label: 'Keep image aspect ratio', value: 'keep_image_aspect_ratio' },

  { label: 'Fixed', value: 'fixed' },
]

const HeightInputsContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`

const HeightModeSelectContainer = styled.div`
  flex: 0 0 170px;
  margin-right: 6px;
`

const HeightValueInputContainer = styled.div`
  flex: 0 0 70px;
  margin-right: 6px;
`
